import Image from 'next/image'
import { Colors } from '@kvika/audur-theme'
import { useEffect, useState } from 'react'

import { TranslatedStrings } from '../../../utils/translations/TranslatedStrings'
import { formatNumberIS } from '../../../utils/Utils'
import { CalculatorPrimary } from '../../../prismic/prismicTypes'
import AudurTooltip from '../../../components/audurTooltip/AudurTooltip'

import styles from './newCalculatorContainers.module.scss'

export type NewCalculatorSummaryProps = {
	title: string
	svg: string
	desciption: string
	interestRate: string
	payedMonthly: number
	estimatedEquityAfterXYears: number
	isPaymentReductionPlan: boolean
	calculatorPrismicTooltipData: CalculatorPrimary
	objectName: string
	showOnMobile: boolean
	propertyEquity?: number
	durationOfReduction?: number
	reductionPaymentAfterXYears?: number
}

const NewCalculatorSummary = (props: NewCalculatorSummaryProps) => {
	const {
		title,
		svg,
		desciption,
		interestRate,
		payedMonthly,
		estimatedEquityAfterXYears: estimatedEquityAfter5Years,
		isPaymentReductionPlan,
		durationOfReduction,
		propertyEquity,
		calculatorPrismicTooltipData,
		reductionPaymentAfterXYears,
		objectName,
		showOnMobile,
	} = props

	const [monthlyPaymentAfterXYearsString, setMonthlyPaymentAfterXYearsString] = useState<string>('')

	// Determine if this is the "Equal Payments" option
	const isEqualPayments = objectName === 'EqualPayments'
	// Get tooltip text based on plan type
	const tooltipText = !isPaymentReductionPlan
		? (calculatorPrismicTooltipData.equalization_tooltip ??
			TranslatedStrings.is.newCalculator.defaultEqualizationTooltip)
		: (calculatorPrismicTooltipData.reduction_tooltip ?? TranslatedStrings.is.newCalculator.defaultReductionTooltip)
	const summaryImageClass = isEqualPayments ? styles.redSummaryImage : styles.greenSummaryImage

	useEffect(() => {
		if (!durationOfReduction) {
			setMonthlyPaymentAfterXYearsString(TranslatedStrings.is.newCalculator.monthlyPaymentAfter3Years)
			return
		}
		const newTitle = TranslatedStrings.is.newCalculator.monthlyPaymentAfter3Years.replace(
			'XX',
			durationOfReduction.toString()
		)

		setMonthlyPaymentAfterXYearsString(newTitle)
	}, [durationOfReduction])

	return (
		<div className={[styles.summary, showOnMobile ? styles.showOnMobile : ''].join(' ')}>
			<div className={styles.summaryTop}>
				<div className={`${styles.summaryImage} ${summaryImageClass}`}>
					<Image src={svg} width={24} alt='SummaryIcon' height={24} className={styles.summaryIcon} priority />
				</div>
				<div className={styles.summaryHeader}>
					<div className={styles.summaryTitle}>{title}</div>
					<div className={styles.summaryDescription}>
						{desciption}
						<AudurTooltip
							text={tooltipText.replace('XX', interestRate.toString())}
							infoOutlinedColor={Colors.LightGray}
							activeInfoOutlinedColor={Colors.Silver}
							backgroundColor={Colors.Silver}
							hideCloseIcon
							hideTitleRow
						/>
					</div>
				</div>
			</div>

			<div className={styles.summaryTop}>
				{objectName !== 'EqualPayments' ? (
					<div className={styles.summaryPlan}>
						<div className={styles.textTooltip}>
							{TranslatedStrings.is.newCalculator.monthlyPayment}
							<AudurTooltip
								text={
									calculatorPrismicTooltipData.estimated_monthly_payment_tooltip ??
									TranslatedStrings.is.newCalculator.defaultMonthlyInstallmentTooltip
								}
								infoOutlinedColor={Colors.LightGray}
								activeInfoOutlinedColor={Colors.Silver}
								backgroundColor={Colors.Silver}
								hideCloseIcon
								hideTitleRow
							/>
						</div>
						<div className={styles.summaryPlanPriceBig}>{formatNumberIS(payedMonthly)} kr.</div>
					</div>
				) : (
					<div className={styles.summaryPlan}>
						<div className={styles.textTooltip}>
							{monthlyPaymentAfterXYearsString}
							<AudurTooltip
								text={
									calculatorPrismicTooltipData.estimated_monthly_payment_tooltip ??
									TranslatedStrings.is.newCalculator.defaultMonthlyInstallmentTooltip
								}
								infoOutlinedColor={Colors.LightGray}
								activeInfoOutlinedColor={Colors.Silver}
								backgroundColor={Colors.Silver}
								hideCloseIcon
								hideTitleRow
							/>
						</div>
						<div className={styles.summaryPlanPriceBig}>{formatNumberIS(reductionPaymentAfterXYears ?? 0)} kr.</div>
					</div>
				)}
			</div>

			<div className={styles.summaryMiddle}>
				{renderMarketValueSection()}
				{renderEstimatedEquitySection()}
			</div>
		</div>
	)

	function renderMarketValueSection() {
		return (
			<div className={styles.summaryPlan}>
				<div className={styles.textTooltip}>
					{TranslatedStrings.is.newCalculator.marketValueLoan}
					<AudurTooltip
						text={
							calculatorPrismicTooltipData.property_equity_tooltip ??
							TranslatedStrings.is.newCalculator.defaultPropertyEquityTooltip
						}
						infoOutlinedColor={Colors.LightGray}
						activeInfoOutlinedColor={Colors.Silver}
						backgroundColor={Colors.Silver}
						hideCloseIcon
						hideTitleRow
					/>
				</div>
				<div className={styles.summaryPlanPrice}>{propertyEquity ? `${formatNumberIS(propertyEquity)} kr.` : '-'}</div>
			</div>
		)
	}

	function renderEstimatedEquitySection() {
		return (
			<div className={styles.summaryPlan}>
				<div className={styles.textTooltip}>
					<p className={styles.textDesktop}>
						{TranslatedStrings.is.newCalculator.estimatedEquityAfterXYears.replace(
							'XX',
							(durationOfReduction ?? 3).toString()
						)}
					</p>
					<p className={styles.textMobile}>
						{TranslatedStrings.is.newCalculator.estimatedEquityAfterXYearsShort.replace(
							'XX',
							(durationOfReduction ?? 3).toString()
						)}
					</p>
					<AudurTooltip
						text={(
							calculatorPrismicTooltipData.estimated_equity_x_years_tooltip ??
							TranslatedStrings.is.newCalculator.defaultEstimatedEquityxYearsTooltip
						).replace('XX', (durationOfReduction ?? 3).toString())}
						infoOutlinedColor={Colors.LightGray}
						activeInfoOutlinedColor={Colors.Silver}
						backgroundColor={Colors.Silver}
						hideCloseIcon
						hideTitleRow
					/>
				</div>
				<div className={styles.summaryPlanPrice}>{formatNumberIS(estimatedEquityAfter5Years)} kr.</div>
			</div>
		)
	}
}

export default NewCalculatorSummary
